// Provides a single global ad-gate + trial-used flow that any page can call:
//
//   const { runGate } = useFreeTierGate();
//   const ok = await runGate(); // true = proceed with generation, false = stop
//
// Internally:
//   • Calls checkFreeTierGate() to decide.
//   • If 'watch-ad' → opens AdGateDialog and awaits result.
//   • If 'trial-used' → opens FreeTrialUsedDialog (with 'See plans' → BuyCreditsDialog).
//   • If 'allow' → resolves immediately.
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { checkFreeTierGate } from "@/lib/free-tier-gate";
import { AdGateDialog } from "@/components/AdGateDialog";
import { FreeTrialUsedDialog } from "@/components/FreeTrialUsedDialog";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";

interface Ctx {
  /** Returns true if caller may proceed with generation, false if blocked/cancelled. */
  runGate: () => Promise<boolean>;
}

const FreeTierGateContext = createContext<Ctx | null>(null);

export const FreeTierGateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  const [adOpen, setAdOpen] = useState(false);
  const [adGenNum, setAdGenNum] = useState(2);
  const [trialUsedOpen, setTrialUsedOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  const adResolverRef = useRef<((v: boolean) => void) | null>(null);

  const runGate = useCallback(async (): Promise<boolean> => {
    if (!user) return true; // no user = something else will block
    const result = await checkFreeTierGate(user.id);
    if (result.action === "allow") return true;
    if (result.action === "trial-used") {
      setTrialUsedOpen(true);
      return false;
    }
    // watch-ad
    setAdGenNum(result.generationNumber);
    setAdOpen(true);
    return new Promise<boolean>((resolve) => {
      adResolverRef.current = resolve;
    });
  }, [user]);

  const handleAdResolve = useCallback((ok: boolean) => {
    setAdOpen(false);
    const r = adResolverRef.current;
    adResolverRef.current = null;
    r?.(ok);
  }, []);

  return (
    <FreeTierGateContext.Provider value={{ runGate }}>
      {children}
      <AdGateDialog
        open={adOpen}
        generationNumber={adGenNum}
        onResolve={handleAdResolve}
      />
      <FreeTrialUsedDialog
        open={trialUsedOpen}
        onOpenChange={setTrialUsedOpen}
        onSeePlans={() => {
          setTrialUsedOpen(false);
          setBuyOpen(true);
        }}
      />
      <BuyCreditsDialog
        open={buyOpen}
        onOpenChange={setBuyOpen}
        outOfCredits
      />
    </FreeTierGateContext.Provider>
  );
};

export const useFreeTierGate = (): Ctx => {
  const ctx = useContext(FreeTierGateContext);
  if (!ctx) {
    // Soft-fallback — pages outside the provider get a no-op that always allows.
    // This prevents crashes in tests/storybook while still working in the real app.
    return { runGate: async () => true };
  }
  return ctx;
};
